import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import { FiInfo } from 'react-icons/fi'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import { getLoanCandidates } from 'services/fund'
import { faPlain } from 'utilities/helper/format'
import {
    DEMAND_PRIORITIES,
    demandPriority,
} from 'utilities/helper/demandPriority'
import CandidateRow from '../components/CandidateRow'
import { ScoreExplainer } from 'components/memberScore'

/**
 * A filter as a pill rather than a checkbox row with a two-line hint.
 *
 * The explanations matter, but spelled out they pushed the first candidate
 * 380px down a 932px screen — the list is the screen, and a manager scrolling
 * past a wall of help text to reach it is the failure mode. The «چرا؟» keeps
 * the wording, one tap away.
 */
function FilterPill({
    checked,
    onChange,
    label,
}: {
    checked: boolean
    onChange: (next: boolean) => void
    label: string
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={() => onChange(!checked)}
            className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold transition-colors ${
                checked
                    ? ' border-brand bg-brand-soft text-brand-deep'
                    : ' border-stroke text-text-secondary'
            }`}
        >
            {label}
        </button>
    )
}

/**
 * The رتبهٔ اعتباری filter's steps, as a floor on the percentile.
 *
 * A percentile floor rather than «رتبهٔ ۱ تا ۱۰», because rank counts mean
 * different things in a 12-member fund and a 579-member one, and the row
 * already states itself in this language («بالاتر از ۹۷٪»). `0` is «همه» —
 * no filter — not «بالاتر از ۰٪», which would still drop anyone the ranking
 * put at the very bottom.
 */
const RANK_STEPS = [
    // The «فقط» prefix is not decoration: ScoreMeter labels every bar on the
    // list «بالاتر از N درصد اعضا», so a bare «بالاتر از ۷۵ درصد اعضا» here
    // would be the same accessible name as a row's meter — ambiguous to a
    // screen reader, and to anything else looking the control up by name.
    { value: 0, label: 'همه', aria: 'نمایش همهٔ رتبه‌ها' },
    { value: 50, label: '۵۰٪+', aria: 'فقط بالاتر از ۵۰ درصد اعضا' },
    { value: 75, label: '۷۵٪+', aria: 'فقط بالاتر از ۷۵ درصد اعضا' },
    { value: 90, label: '۹۰٪+', aria: 'فقط بالاتر از ۹۰ درصد اعضا' },
]

/**
 * The فوریت the member picked when they filed, as a filter.
 *
 * Not «چند روز پیش ثبت شده» — that was the wrong question. How long a request
 * has waited is the fund's doing, not the member's, and filtering by it hides
 * exactly the people the queue has already failed. What a manager wants to
 * narrow by is what the member said about their own need, which is the one
 * thing the request form asks: «فوری(کمتر از ۳ روز)» down to
 * «پایین(بیشتر از ۳۰ روز)».
 *
 * The empty value is «همه» — a filter being off is not a fifth level anyone
 * could have chosen.
 */
/**
 * The two ends of the scale, for the help text — read from the same table
 * the chips and the filter read, so the explanation cannot drift from the
 * thing it explains.
 */
const urgentMeta = demandPriority('URGENT')!
const lowMeta = demandPriority('LOW')!

const PRIORITY_STEPS = [
    { value: '', label: 'همه', aria: 'درخواست‌ها با هر فوریتی' },
    ...DEMAND_PRIORITIES.map((key) => {
        const meta = demandPriority(key)!
        return {
            value: key as string,
            label: meta.label,
            // «بالا» out loud is a direction, not an urgency; the deadline
            // the word stands for is what makes the option readable
            aria: `فقط فوریت ${meta.label}`,
        }
    }),
]

/**
 * One of the two filter rows — a radio group, not four switches.
 *
 * Separate from `FilterPill` because the two are different questions: those
 * change what the server ranks, these hide part of an answer already on
 * screen. Keeping the shapes similar but the roles distinct (`radio` vs
 * `switch`) is what tells a screen reader that the four are exclusive.
 *
 * The two rows are independent and combine: «۷۵٪+ از میان درخواست‌های ۳۰ روز
 * اخیر» is a question a manager actually has, and neither filter alone
 * answers it.
 */
function ChoiceFilter<T extends string | number>({
    label,
    steps,
    value,
    onChange,
}: {
    label: string
    steps: { value: T; label: string; aria: string }[]
    value: T
    onChange: (next: T) => void
}) {
    return (
        <div
            role="radiogroup"
            aria-label={`فیلتر ${label}`}
            className=" mt-2 flex items-center gap-2"
        >
            <span className=" w-[68px] shrink-0 text-[11px] text-text-secondary">
                {label}
            </span>
            <div className=" flex flex-1 items-center gap-1.5">
                {steps.map((step) => (
                    <button
                        key={String(step.value)}
                        type="button"
                        role="radio"
                        aria-checked={value === step.value}
                        aria-label={step.aria}
                        onClick={() => onChange(step.value)}
                        className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold tabular-nums transition-colors ${
                            value === step.value
                                ? ' border-brand bg-brand-soft text-brand-deep'
                                : ' border-stroke text-text-secondary'
                        }`}
                    >
                        {step.label}
                    </button>
                ))}
            </div>
        </div>
    )
}

/**
 * Step 1 — «به چه کسی؟».
 *
 * This is the question the old screen never asked. It opened on «مبلغ وام»,
 * which is the second question; the first one a manager of a 500-member fund
 * answers from memory, standing in front of the person asking.
 *
 * So the list is ranked by امتیاز عضو and the two things that change the
 * shape of the list are checkboxes, not settings: whether to look past the
 * request queue, and whether to count old money at what it was worth.
 */
export default function StepBorrower({
    moneypoolId,
    selectedPoolshipId,
    onSelect,
}: {
    moneypoolId?: string
    selectedPoolshipId?: number
    onSelect: (candidate: ILoanCandidate) => void
}) {
    const [requestsOnly, setRequestsOnly] = useState(true)
    const [inflation, setInflation] = useState(false)
    const [minPercentile, setMinPercentile] = useState(0)
    const [priority, setPriority] = useState('')
    const [search, setSearch] = useState('')
    const [showFilterHelp, setShowFilterHelp] = useState(false)
    const [explaining, setExplaining] = useState<ILoanCandidate | null>(null)

    const { data, isLoading, isError, isFetching, isPreviousData } = useQuery(
        ['loan-candidates', moneypoolId, requestsOnly, inflation],
        getLoanCandidates,
        { enabled: !!moneypoolId, staleTime: 60_000, keepPreviousData: true }
    )

    /**
     * `keepPreviousData` keeps the OLD ranking on screen while the new one is
     * fetched, which is right — the list must not blink out from under a
     * finger. But nothing said the numbers being read were the previous
     * answer, so «تعدیل تورمی» looked like a switch that flips itself and
     * changes nothing: the pill went green, every row stayed exactly as it
     * was, and managers toggled it over and over (17 people, 179 toggles).
     */
    const recomputing = isFetching && isPreviousData

    /**
     * The رتبهٔ اعتباری floor, applied before the name search so the «چند نفر
     * پنهان شدند» count below means what it says: hidden by the filter, not
     * by whatever was typed in the box.
     *
     * Client-side on purpose. The list is already here — the server ranks the
     * whole fund and caps at 600 — so a filter that hides part of it needs no
     * round trip, and the manager gets it while their finger is still on the
     * pill.
     */
    const ranked = useMemo(() => {
        const results = data?.results ?? []
        return results.filter((row) => {
            if (minPercentile && row.percentile < minPercentile) return false
            if (!priority) return true
            // A member with no open request never picked a فوریت, so this
            // filter has nothing to say about them — «درخواست‌های فوری» that
            // lists people who never asked is not that filter.
            return row.demand?.priority === priority
        })
    }, [data, minPercentile, priority])

    const rows = useMemo(() => {
        const term = search.trim()
        if (!term) return ranked
        return ranked.filter((row) => row.member_name.includes(term))
    }, [ranked, search])

    // Never let the filter look like an empty queue. A manager who set a
    // floor and then sees «هیچ درخواستی ندارید» would go looking for a bug
    // in the requests, not for the pill they just tapped.
    const hiddenByFilters = (data?.results.length ?? 0) - ranked.length
    const anyFilter = !!minPercentile || !!priority

    // One event per search, once the typing stops — a capture per keystroke
    // would drown the funnel in a name's worth of events. What is worth
    // knowing is whether the search found anybody, which is why it waits for
    // the settled query rather than firing on the first character.
    //
    // The typed name never leaves the device: only how long it was, and how
    // many rows it matched.
    useEffect(() => {
        const term = search.trim()
        if (!term) return
        const timer = setTimeout(() => {
            posthog.capture('loan_candidates_searched', {
                fund_id: moneypoolId,
                query_length: term.length,
                result_count: rows.length,
                requests_only: requestsOnly,
                // `result_count` is counted after the rank floor, so the
                // floor has to travel with it or a zero looks like the
                // search failing
                min_percentile: minPercentile,
                priority: priority || null,
                // a truncated list is the case where searching is the only way
                // to reach a member at all
                truncated: !!data?.truncated,
            })
        }, 700)
        return () => clearTimeout(timer)
    }, [
        search,
        rows.length,
        moneypoolId,
        requestsOnly,
        minPercentile,
        priority,
        data?.truncated,
    ])

    if (isLoading && !data) {
        return (
            <div className=" py-10">
                <DotsLoading />
            </div>
        )
    }

    if (isError) {
        return (
            <div className=" rounded-2xl border border-stroke bg-white p-4 text-center">
                <p className=" text-xs leading-6 text-text-secondary">
                    فهرست اعضا الان در دسترس نیست. می‌توانید دوباره تلاش کنید.
                </p>
            </div>
        )
    }

    // keyed off the rows we actually got, not the demands counter: if the
    // two ever disagree, hiding real candidates behind «هیچ درخواستی ندارید»
    // is the worse failure
    const emptyQueue = requestsOnly && (data?.results.length ?? 0) === 0

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-3">
                <div className=" flex items-center gap-2 rounded-xl bg-canvas px-3 py-2">
                    <CiSearch size={18} className=" shrink-0 text-text-muted" />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="جستجوی نام عضو"
                        aria-label="جستجوی نام عضو"
                        className=" w-full bg-transparent text-xs outline-none placeholder:text-text-muted"
                    />
                </div>

                <div className=" mt-2 flex items-center gap-2">
                    <FilterPill
                        checked={!requestsOnly}
                        onChange={(next) => {
                            setRequestsOnly(!next)
                            posthog.capture('loan_candidates_scope_changed', {
                                fund_id: moneypoolId,
                                include_all: next,
                            })
                        }}
                        label="همهٔ اعضا"
                    />
                    <FilterPill
                        checked={inflation}
                        onChange={(next) => {
                            setInflation(next)
                            posthog.capture(
                                'loan_candidates_inflation_toggled',
                                {
                                    fund_id: moneypoolId,
                                    inflation: next,
                                }
                            )
                        }}
                        label="تعدیل تورمی"
                    />
                    <button
                        type="button"
                        aria-label="راهنمای فیلترها"
                        onClick={() => setShowFilterHelp((open) => !open)}
                        className=" -m-1 shrink-0 p-1 text-text-muted"
                    >
                        <FiInfo size={15} />
                    </button>
                </div>
                <ChoiceFilter
                    label="رتبهٔ اعتباری"
                    steps={RANK_STEPS}
                    value={minPercentile}
                    onChange={(next) => {
                        setMinPercentile(next)
                        posthog.capture('loan_candidates_rank_filtered', {
                            fund_id: moneypoolId,
                            min_percentile: next,
                            priority: priority || null,
                            requests_only: requestsOnly,
                            total_count: data?.results.length ?? 0,
                        })
                    }}
                />
                <ChoiceFilter
                    label="فوریت"
                    steps={PRIORITY_STEPS}
                    value={priority}
                    onChange={(next) => {
                        setPriority(next)
                        posthog.capture('loan_candidates_priority_filtered', {
                            fund_id: moneypoolId,
                            priority: next || null,
                            min_percentile: minPercentile,
                            requests_only: requestsOnly,
                            total_count: data?.results.length ?? 0,
                        })
                    }}
                />
                {showFilterHelp && (
                    <div className=" mt-2 rounded-xl bg-canvas p-2.5 text-[11px] leading-5 text-text-secondary">
                        <p>
                            <b className=" text-text-body">همهٔ اعضا:</b> به‌طور
                            پیش‌فرض فقط کسانی را می‌بینید که درخواست وام
                            داده‌اند. با این گزینه، همهٔ اعضای صندوق نمایش داده
                            می‌شوند.
                        </p>
                        <p className=" mt-1.5">
                            <b className=" text-text-body">تعدیل تورمی:</b> ۱
                            میلیون تومان چند سال پیش بیشتر از ۱ میلیون امروز
                            بوده؛ با شاخص قیمت مصرف‌کننده به پول امروز تبدیل
                            می‌شود.
                        </p>
                        <p className=" mt-1.5">
                            <b className=" text-text-body">رتبهٔ اعتباری:</b>{' '}
                            جای عضو در امتیاز صندوق. «۷۵٪+» یعنی فقط کسانی که
                            رتبه‌شان از سه‌چهارم اعضا بالاتر است. فهرست همیشه بر
                            اساس همین امتیاز مرتب است؛ این فیلتر فقط انتهای آن
                            را پنهان می‌کند.
                        </p>
                        <p className=" mt-1.5">
                            <b className=" text-text-body">فوریت:</b> همان چیزی
                            که خود عضو موقع ثبت درخواست انتخاب کرده. «
                            {urgentMeta.label}» یعنی «{urgentMeta.need}» و «
                            {lowMeta.label}» یعنی «{lowMeta.need}» — شمرده از
                            روزی که درخواست ثبت شده. دو فیلتر با هم کار می‌کنند.
                        </p>
                    </div>
                )}
                {inflation && data && !data.inflation.applied && (
                    <p className=" mt-2 rounded-lg bg-warning-soft px-2 py-1.5 text-[11px] leading-5 text-warning-on">
                        شاخص تورم هنوز روی سرور بارگذاری نشده، پس فعلاً بدون
                        تعدیل محاسبه شده است.
                    </p>
                )}
            </div>

            {/* The order is stated at the list, not only in the intro
                paragraph above it — by the time a manager is comparing two
                rows they have scrolled past the paragraph, and «why is this
                one first?» is exactly the question the answer belongs next
                to. */}
            <p className=" mt-3 px-1 text-[11px] text-text-secondary">
                {requestsOnly
                    ? `${faPlain.format(
                          data?.demands_count ?? 0
                      )} نفر درخواست وام داده‌اند، از بالاترین رتبهٔ اعتباری به پایین`
                    : `${faPlain.format(
                          data?.members_count ?? 0
                      )} عضو، از بالاترین رتبهٔ اعتباری به پایین`}
                {hiddenByFilters > 0 && (
                    <>
                        {' — '}
                        {faPlain.format(hiddenByFilters)} نفر با فیلترهای فعلی
                        پنهان شده‌اند
                    </>
                )}
            </p>

            {emptyQueue ? (
                <div className=" mt-2 rounded-2xl border border-stroke bg-white p-4 text-center">
                    <p className=" text-xs font-bold text-text-body">
                        هیچ درخواست وام بازی ندارید
                    </p>
                    <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                        برای اعطای وام به عضوی که حضوری درخواست داده، «نمایش
                        همهٔ اعضا» را روشن کنید.
                    </p>
                </div>
            ) : rows.length === 0 ? (
                <div className=" mt-2 rounded-2xl border border-stroke bg-white p-4 text-center">
                    {/* The filter is named as the cause when it is the cause,
                        with the way out in the same sentence — otherwise an
                        empty list reads as «هیچ‌کس درخواست نداده» and the
                        manager goes looking in the wrong place. */}
                    {!search.trim() && anyFilter && hiddenByFilters > 0 ? (
                        <>
                            <p className=" text-xs font-bold text-text-body">
                                کسی با این فیلترها نمانده
                            </p>
                            <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                                {faPlain.format(hiddenByFilters)} نفر پنهان
                                شده‌اند. برای دیدن‌شان فیلترها را روی «همه»
                                بگذارید.
                            </p>
                        </>
                    ) : (
                        <p className=" text-xs text-text-secondary">
                            عضوی با این نام پیدا نشد.
                        </p>
                    )}
                </div>
            ) : (
                <div
                    className={` mt-2 space-y-2 transition-opacity ${
                        recomputing ? ' pointer-events-none opacity-50' : ''
                    }`}
                    aria-busy={recomputing}
                >
                    {rows.map((candidate) => (
                        <CandidateRow
                            key={candidate.poolship_id}
                            candidate={candidate}
                            moneypoolId={moneypoolId}
                            isSelected={
                                candidate.poolship_id === selectedPoolshipId
                            }
                            onSelect={() => onSelect(candidate)}
                            onExplain={() => {
                                setExplaining(candidate)
                                posthog.capture('loan_score_explained', {
                                    fund_id: moneypoolId,
                                    poolship_id: candidate.poolship_id,
                                })
                            }}
                        />
                    ))}
                </div>
            )}

            {data?.truncated && (
                <p className=" mt-2 px-1 text-[11px] text-text-secondary">
                    فهرست طولانی است و فقط بخشی از آن نمایش داده شده؛ برای یافتن
                    عضو خاص از جستجو استفاده کنید.
                </p>
            )}

            <ScoreExplainer
                show={!!explaining}
                onHide={() => setExplaining(null)}
                candidate={explaining}
                overdueMultiplier={data?.overdue_multiplier ?? 3}
                earlyMultiplier={data?.early_multiplier ?? 3}
                membersCount={data?.members_count ?? 0}
                inflationApplied={!!data?.inflation.applied}
            />
        </>
    )
}
